/* src/utils/cryptoUtils.js
  ---------------------------------------------------------------------------
  A comprehensive cryptographic utility using the native Web Crypto API.
  Handles:
  1. RSA Key Generation (Company & User Identities)
  2. PBKDF2 Key Derivation (Password-based encryption)
  3. Envelope Encryption/Decryption (RSA-OAEP)
  4. Data Encryption/Decryption (AES-GCM)
  5. Secure Key Sharing (Hybrid RSA+AES Wrapping)
  ---------------------------------------------------------------------------
*/

// ==========================================
// SECTION 1: HELPER FUNCTIONS (Base64 <-> Buffer)
// ==========================================

export function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export function base64ToUint8Array(base64) {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// ==========================================
// SECTION 2: KEY GENERATION (Company & User)
// ==========================================

/**
 * Generates a new RSA Key Pair.
 * Used for:
 * 1. Creating a Company Identity (Company_Keys)
 * 2. Creating a User Identity (User_Keys)
 */
export async function generateRSAKeyPair() {
  return window.crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true, 
    ["encrypt", "decrypt"]
  );
}

// Alias for readability in code
export const generateCompanyKeyPair = generateRSAKeyPair;
export const generateUserKeyPair = generateRSAKeyPair;

/**
 * Exports a Public Key to PEM format.
 * This is safe to share in the database.
 */
export async function exportPublicKeyToPEM(key) {
  const exported = await window.crypto.subtle.exportKey("spki", key);
  const base64 = arrayBufferToBase64(exported);
  return `-----BEGIN PUBLIC KEY-----\n${base64}\n-----END PUBLIC KEY-----`;
}

/**
 * Exports a Private Key Object to a PEM String.
 * Use this to prepare the key for sessionStorage.
 */
export async function exportPrivateKeyToPEM(privateKeyObj) {
  // Export to "pkcs8" format (Standard for Private Keys)
  const exported = await window.crypto.subtle.exportKey("pkcs8", privateKeyObj);
  const base64 = arrayBufferToBase64(exported);
  return `-----BEGIN PRIVATE KEY-----\n${base64}\n-----END PRIVATE KEY-----`;
}

/**
 * Imports a PEM String (Public Key) into a CryptoKey Object.
 * Used when we need to encrypt data for a Company or User.
 */
export async function importPublicKey(pemKey) {
  const pemHeader = "-----BEGIN PUBLIC KEY-----";
  const pemFooter = "-----END PUBLIC KEY-----";
  
  const pemContents = pemKey
    .replace(pemHeader, "")
    .replace(pemFooter, "")
    .replace(/\s/g, "");

  const binaryDer = base64ToUint8Array(pemContents);

  return window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
}

/**
 * Imports a PEM String (from sessionStorage) back into a Private Key Object.
 */
export async function importPrivateKey(pemKey) {
  const pemHeader = "-----BEGIN PRIVATE KEY-----";
  const pemFooter = "-----END PRIVATE KEY-----";
  const pemContents = pemKey.replace(pemHeader, "").replace(pemFooter, "").replace(/\s/g, "");

  const binaryDer = base64ToUint8Array(pemContents);

  return window.crypto.subtle.importKey(
    "pkcs8",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["decrypt"]
  );
}

// ==========================================
// SECTION 3: PASSWORD-BASED VAULTS
// ==========================================

/**
 * "Locks" a Private Key using a Password.
 * IMPORTANT: Exports as PEM format for backend compatibility
 */
export async function lockPrivateKeyInVault(privateKeyObj, password) {
  // 1. Export Private Key to PEM format
  const pemKey = await exportPrivateKeyToPEM(privateKeyObj);
  const privateKeyBytes = new TextEncoder().encode(pemKey);

  // 2. Generate salt
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  
  // 3. Derive Key from Password
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw", 
    new TextEncoder().encode(password), 
    "PBKDF2", 
    false, 
    ["deriveKey"]
  );

  const wrappingKey = await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: 400000, 
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );

  // 4. Encrypt (auth tag automatically appended by Web Crypto)
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    wrappingKey,
    privateKeyBytes
  );

  return {
    encryptedBlob: arrayBufferToBase64(encryptedBuffer),
    iv: arrayBufferToBase64(iv),
    salt: arrayBufferToBase64(salt)
  };
}

export const encryptPrivateKeyWithPassword = lockPrivateKeyInVault;

/**
 * Helper function to convert PEM to DER for import
 */
function pemToDer(pemString) {
  const pemHeader = "-----BEGIN PRIVATE KEY-----";
  const pemFooter = "-----END PRIVATE KEY-----";
  
  if (!pemString.includes(pemHeader) || !pemString.includes(pemFooter)) {
    throw new Error('Invalid PEM format: missing headers');
  }
  
  const pemContents = pemString
    .replace(pemHeader, '')
    .replace(pemFooter, '')
    .replace(/\s/g, '');
  
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(pemContents)) {
    throw new Error('Invalid PEM format: contains non-base64 characters');
  }
  
  const binaryDer = atob(pemContents);
  return Uint8Array.from(binaryDer, c => c.charCodeAt(0));
}

/**
 * "Unlocks" a Private Key using a Password.
 */
export async function unlockPrivateKeyFromVault(encryptedBlobBase64, password, saltBase64, ivBase64) {
  try {
    const salt = Uint8Array.from(atob(saltBase64), c => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
    const encryptedBytes = Uint8Array.from(atob(encryptedBlobBase64), c => c.charCodeAt(0));
  
    const keyMaterial = await window.crypto.subtle.importKey(
      "raw", 
      new TextEncoder().encode(password), 
      "PBKDF2", 
      false, 
      ["deriveKey"]
    );
  
    const unwrappingKey = await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 400000,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );
  
    // Decrypt (auth tag is automatically handled by Web Crypto)
    const privateKeyBytes = await window.crypto.subtle.decrypt(
      { 
        name: "AES-GCM", 
        iv: iv
      },
      unwrappingKey,
      encryptedBytes
    );
    
    // Convert PEM to DER for import
    const pemString = new TextDecoder().decode(privateKeyBytes);
    const derBytes = pemToDer(pemString);
  
    return window.crypto.subtle.importKey(
      "pkcs8",
      derBytes,
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["decrypt"]
    );
  } catch (error) {
    console.error('Error in unlockPrivateKeyFromVault:', error);
    throw error;
  }
}

/**
 * Unwraps a shared company key
 */
export async function unwrapSharedKey(encryptedSecretBase64, encryptedKeyBase64, ivBase64, myPrivateKey) {
  try {
    const encryptedAesKeyBytes = base64ToUint8Array(encryptedKeyBase64);
    const encryptedSecretBytes = base64ToUint8Array(encryptedSecretBase64);
    const iv = base64ToUint8Array(ivBase64);

    // 1. Decrypt the AES Key using My Private Key
    const aesKeyRaw = await window.crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      myPrivateKey,
      encryptedAesKeyBytes
    );

    const aesKey = await window.crypto.subtle.importKey(
      "raw", 
      aesKeyRaw, 
      "AES-GCM", 
      false, 
      ["decrypt"]
    );

    // 2. Decrypt the Secret Key (Company Key) using the AES Key
    // Auth tag is automatically handled by Web Crypto
    const secretKeyBytes = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv },
      aesKey,
      encryptedSecretBytes
    );

    // 3. Convert PEM to DER
    const decryptedString = new TextDecoder().decode(secretKeyBytes);
    
    if (!decryptedString.includes('-----BEGIN PRIVATE KEY-----')) {
      throw new Error('Invalid key format: not a valid PEM private key');
    }

    const derBytes = pemToDer(decryptedString);

    // 4. Import as usable CryptoKey
    return window.crypto.subtle.importKey(
      "pkcs8",
      derBytes,
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["decrypt"]
    );
  } catch (error) {
    console.error('Error in unwrapSharedKey:', error);
    throw error;
  }
}

// ==========================================
// SECTION 4: CASE DECRYPTION
// ==========================================

export async function decryptEnvelope(encryptedEnvelopeBase64, privateKeyObj) {
  const encryptedBytes = base64ToUint8Array(encryptedEnvelopeBase64);

  const decryptedKeyBuffer = await window.crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKeyObj,
    encryptedBytes
  );

  return window.crypto.subtle.importKey(
    "raw",
    decryptedKeyBuffer,
    "AES-GCM",
    true,
    ["decrypt", "encrypt"]
  );
}

export async function decryptData(encryptedDataBase64, aesKeyObj) {
  const rawData = base64ToUint8Array(encryptedDataBase64);
  const iv = rawData.slice(0, 12);
  const ciphertext = rawData.slice(12); // Includes auth tag (auto-handled by Web Crypto)

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv },
    aesKeyObj,
    ciphertext
  );

  return new TextDecoder().decode(decryptedBuffer);
}

// ==========================================
// SECTION 5: DATA ENCRYPTION
// ==========================================

export async function encryptData(plainText, aesKeyObj) {
  const encoded = new TextEncoder().encode(plainText);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const ciphertextBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    aesKeyObj,
    encoded
  );

  const combined = new Uint8Array(iv.length + ciphertextBuffer.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertextBuffer), iv.length);

  return arrayBufferToBase64(combined.buffer);
}

// ==========================================
// SECTION 6: KEY SHARING (IDENTITY BASED)
// ==========================================

/**
 * WRAPS (Encrypts) a Secret Key for another User.
 * IMPORTANT: Exports as PEM format for backend compatibility
 */
export async function wrapKeyForUser(keyToShare, receiverPublicKeyPem) {
  // 1. Import Receiver's Public Key
  const pubKeyClean = receiverPublicKeyPem.replace(/(-----(BEGIN|END) PUBLIC KEY-----|\n)/g, "");
  const pubKeyBuffer = base64ToUint8Array(pubKeyClean);
  const receiverKey = await window.crypto.subtle.importKey(
    "spki",
    pubKeyBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"]
  );

  // 2. Export the Secret Key to PEM format
  const pemKey = await exportPrivateKeyToPEM(keyToShare);
  const keyBytes = new TextEncoder().encode(pemKey);

  // 3. Generate Ephemeral AES Key
  const tempAesKey = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  
  // 4. Encrypt the Secret Key using AES (auth tag auto-appended)
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedSecret = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    tempAesKey,
    keyBytes
  );

  // 5. Encrypt the AES Key using Receiver's RSA Public Key
  const rawAesKey = await window.crypto.subtle.exportKey("raw", tempAesKey);
  const encryptedAesKey = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    receiverKey,
    rawAesKey
  );

  return {
    encrypted_secret: arrayBufferToBase64(encryptedSecret),
    encrypted_key: arrayBufferToBase64(encryptedAesKey),
    iv: arrayBufferToBase64(iv)
  };
}

// Helper to generate random string
function generateRandomString(length) {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  return result;
}

export function generateAccessPass() {
  const caseId = crypto.randomUUID ? crypto.randomUUID() : generateRandomString(32);
  const secretKey = generateRandomString(16);
  
  return {
    caseId,
    secretKey,
    fullPass: `${caseId}:${secretKey}`
  };
}